import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Notice } from '../components/Notice';
import { LogoMark } from '../components/LogoMark';

interface Branding { appName: string; appLogo: string | null; primaryColor: string; secondaryColor: string; accentColor: string }

export function LoginPage() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState<Branding | null>(null);

  useEffect(() => {
    api<Branding>('/branding').then(setBranding).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try { await login(username, password); } catch (value) { setError(value instanceof Error ? value.message : 'Unable to sign in.'); } finally { setBusy(false); }
  };

  const appName = branding?.appName || 'PickleSocial';
  const nameParts = appName.match(/^(.+?)([A-Z][a-z]+)$/) || [appName, appName, ''];

  return (
    <main className="relative grid min-h-screen overflow-hidden bg-[var(--primary)] px-4 py-8 sm:px-6 lg:grid-cols-2 lg:p-8">
      <div className="pointer-events-none absolute -right-28 -top-32 h-96 w-96 rounded-full border-[70px] border-white/5" />
      <div className="pointer-events-none absolute -bottom-40 left-1/3 h-[34rem] w-[34rem] rounded-full accent-bg opacity-10 blur-3xl" />
      <section className="relative z-10 hidden flex-col justify-between rounded-4xl bg-white/5 p-12 text-white ring-1 ring-white/10 lg:flex">
        <LogoMark branding={branding} />
        <div className="max-w-xl">
          <span className="chip mb-6 bg-[var(--accent)] text-[var(--primary)]">Socials, sorted.</span>
          <h1 className="text-6xl font-extrabold leading-[1.04] tracking-[-0.055em]">Less admin.<br/><span className="accent-text">More court time.</span></h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-white/65">Fair fixtures, live scoring and effortless player changes—built for busy tournament organisers.</p>
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm font-bold text-white/60"><span>Up to 30 players</span><span>Four live courts</span><span>Installable PWA</span></div>
      </section>

      <section className="relative z-10 grid place-items-center py-8 lg:py-0">
        <div className="w-full max-w-md rounded-4xl bg-white p-7 shadow-2xl sm:p-10">
          <div className="mb-10 lg:hidden"><LogoMark darkText branding={branding} /></div>
          <p className="eyebrow">Welcome back</p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight brand-text">Sign in to your courts</h2>
          <p className="mt-3 text-sm leading-6 text-slate-500">Your fixtures and branding will be ready where you left them.</p>
          <form className="mt-8 space-y-5" onSubmit={submit}>
            {error && <Notice>{error}</Notice>}
            <label><span className="label">Username</span><input className="field" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></label>
            <div>
              <span className="label">Password</span>
              <div className="relative">
                <input className="field !pr-12" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={6} />
                <button type="button" tabIndex={-1} onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600">
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.4 6.4m3.478 3.478l4.242 4.242m0 0L17.6 17.6M3 3l18 18" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>
            <button className="btn-primary w-full" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          </form>
          <p className="mt-8 text-center text-xs font-semibold text-slate-400">Secure 30-day device session</p>
        </div>
      </section>
    </main>
  );
}
